import { onCLS, onINP, onFCP, onLCP, onTTFB, Metric } from 'web-vitals';

interface PerformanceData {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
  url: string;
  userAgent: string;
}

// Web Vitals thresholds (in milliseconds)
const thresholds = {
  CLS: { good: 0.1, poor: 0.25 },
  INP: { good: 200, poor: 500 },
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  TTFB: { good: 800, poor: 1800 }
};

function getRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = thresholds[name as keyof typeof thresholds];
  if (!threshold) return 'good';
  
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
}

function sendToAnalytics(data: PerformanceData) {
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Core Web Vital:', {
      metric: data.name,
      value: data.value,
      rating: data.rating,
      url: data.url
    });
  }

  // In production, send to your analytics service
  if (process.env.NODE_ENV === 'production') {
    // Example: Send to Google Analytics, DataDog, or custom endpoint
    try {
      fetch('/api/analytics/web-vitals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true
      }).catch(() => {
        // Silently fail in case analytics endpoint is unavailable
      });
    } catch (error) {
      // Silently handle errors to not affect user experience
    }
  }
}

function onPerfEntry(metric: Metric) {
  const data: PerformanceData = {
    name: metric.name,
    value: metric.value,
    rating: getRating(metric.name, metric.value),
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent
  };

  sendToAnalytics(data);
}

export function measureWebVitals() {
  // Only measure in browser environment
  if (typeof window === 'undefined') return;

  try {
    onCLS(onPerfEntry);
    onINP(onPerfEntry);  
    onFCP(onPerfEntry);
    onLCP(onPerfEntry);
    onTTFB(onPerfEntry);
  } catch (error) {
    console.warn('Web Vitals measurement failed:', error);
  }
}

// Performance observer for additional metrics
export function observePerformance() {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  try {
    // Observe navigation timing
    const navObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming;
          const loadTime = navEntry.loadEventEnd - navEntry.fetchStart;
          
          if (process.env.NODE_ENV === 'development') {
            console.log('⚡ Navigation Performance:', {
              loadTime: `${loadTime.toFixed(0)}ms`,
              domContentLoaded: `${(navEntry.domContentLoadedEventEnd - navEntry.fetchStart).toFixed(0)}ms`,
              firstByte: `${(navEntry.responseStart - navEntry.fetchStart).toFixed(0)}ms`
            });
          }
        }
      });
    });

    navObserver.observe({ entryTypes: ['navigation'] });

    // Observe resource loading
    const resourceObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const slowResources = entries.filter(entry => entry.duration > 1000);
      
      if (slowResources.length > 0 && process.env.NODE_ENV === 'development') {
        console.warn('🐌 Slow Resources:', slowResources.map(r => ({
          name: r.name,
          duration: `${r.duration.toFixed(0)}ms`
        })));
      }
    });

    resourceObserver.observe({ entryTypes: ['resource'] });

  } catch (error) {
    console.warn('Performance observation failed:', error);
  }
}

// Bundle analysis helper
export function reportBundleSize() {
  if (typeof window === 'undefined' || process.env.NODE_ENV !== 'development') return;

  // Get all loaded resources
  const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
  const jsResources = resources.filter(r => r.name.includes('.js'));
  const cssResources = resources.filter(r => r.name.includes('.css'));

  console.log('📦 Bundle Analysis:', {
    jsFiles: jsResources.length,
    cssFiles: cssResources.length,
    totalTransferSize: `${(resources.reduce((sum, r) => sum + (r.transferSize || 0), 0) / 1024).toFixed(1)}KB`,
    largestJS: jsResources
      .sort((a, b) => (b.transferSize || 0) - (a.transferSize || 0))
      .slice(0, 3)
      .map(r => ({
        name: r.name.split('/').pop(),
        size: `${((r.transferSize || 0) / 1024).toFixed(1)}KB`
      }))
  });
}